import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { ResetPassword } from '../../../shared/models/rest-password.model';

@Injectable()
export class PasswordRecoveryRepository {
  // Keep headers/options configurable; you can populate this if needed.
  // Using HttpHeaders is typical, but leaving it flexible as per your original code.
  private options: { headers?: HttpHeaders } = {};

  constructor(private http: HttpClient) {}

  /**
   * Recover password for a given username.
   * POST body: { username }
   * Options: observe 'response' + headers from this.options
   */
  recoverPassword(userName: string): Observable<any> {
    const path = environment.user.recoverPassword;
    const body = { username: userName };
    return this.postJson(path, body);
  }

  /**
   * Reset password using token and payload.
   * Path contains template placeholders {username} and {token}.
   * POST body: ResetPassword
   */
  resetPassword(username: string, resetToken: string, passwords: ResetPassword): Observable<any> {
    const path = this.buildPath(environment.user.resetPassword, {
      username,
      token: resetToken,
    });
    return this.postJson(path, passwords);
  }

  /**
   * Validate whether a reset token is still valid.
   * Path contains template placeholders {username} and {token}.
   * GET request observing the full response.
   */
  checkResetTokenValid(username: string, token: string): Observable<any> {
    const path = this.buildPath(environment.user.checkResetPasswordToken, {
      username,
      token,
    });
    return this.getJson(path);
  }

  // -------------------- Helpers (de-duplicate repeated code) --------------------

  /**
   * Replace placeholders in a URL template with actual values.
   * Example:
   *   buildPath('/api/users/{username}/reset/{token}', { username: 'john', token: 'abc' })
   *   => '/api/users/john/reset/abc'
   */
  private buildPath(template: string, params: Record<string, string>): string {
    return Object.keys(params).reduce((acc, key) => acc.replace(`{${key}}`, params[key]), template);
  }

  /**
   * Standard POST with JSON body, observing the full response.
   */
  private postJson<TBody extends object>(url: string, body: TBody): Observable<any> {
    return this.http.post(url, body, {
      observe: 'response',
      headers: this.options.headers,
    });
  }

  /**
   * Standard GET observing the full response.
   */
  private getJson(url: string): Observable<any> {
    return this.http.get(url, {
      observe: 'response',
      headers: this.options.headers,
    });
  }
}
